import type { ApplicationStage } from './types';

/** Money is carried in cents everywhere; nothing renders a raw cent value. */
export function money(cents: number | null | undefined, opts: { cents?: boolean } = {}): string {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: opts.cents ? 2 : 0,
    maximumFractionDigits: opts.cents ? 2 : 0,
  }).format(cents / 100);
}

/** Compact form for chart labels and tiles: $8.48M. */
export function moneyCompact(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const dollars = cents / 100;
  if (Math.abs(dollars) >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(2)}M`;
  if (Math.abs(dollars) >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return money(cents);
}

export function number(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}

export function percent(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(digits)}%`;
}

/** An effective rate arrives as a fraction (0.0241) and reads as 2.41%. */
export function rate(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined) return '—';
  return `${(fraction * 100).toFixed(2)}%`;
}

/** Basis points, as the pipeline shows them: "IC+ 35 bps" / "Flat 2.60%". */
export function pricingLabel(model: string, basisPoints: number): string {
  if (model === 'flat') return `Flat ${(basisPoints / 100).toFixed(2)}%`;
  if (model === 'tiered') return 'Tiered';
  return `IC+ ${basisPoints} bps`;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

export function longDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString(
    'en-GB',
    { hour: '2-digit', minute: '2-digit' },
  )}`;
}

/** "5h", "3d" — the pipeline's Age column. */
export function age(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`;
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function monthShort(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', {
    month: 'short',
    timeZone: 'UTC',
  });
}

// --- stage vocabulary ------------------------------------------------------

export type Tone = 'ok' | 'info' | 'warn' | 'danger' | 'neutral';

const STAGE_META: Record<ApplicationStage, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  awaiting_signature: { label: 'Awaiting signature', tone: 'warn' },
  submitted: { label: 'Submitted', tone: 'info' },
  rate_exception: { label: 'Rate exception', tone: 'warn' },
  in_underwriting: { label: 'In underwriting', tone: 'info' },
  conditions: { label: 'Conditions', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  boarding: { label: 'Approved · boarding', tone: 'ok' },
  boarded: { label: 'Boarded', tone: 'ok' },
  declined: { label: 'Declined', tone: 'danger' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};

export function stageLabel(stage: ApplicationStage): string {
  return STAGE_META[stage]?.label ?? stage;
}

export function stageTone(stage: ApplicationStage): Tone {
  return STAGE_META[stage]?.tone ?? 'neutral';
}

const ORDER_STATUS: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  pending_approval: { label: 'Awaiting approval', tone: 'warn' },
  packing: { label: 'Packing', tone: 'info' },
  shipped: { label: 'Shipped', tone: 'ok' },
  delivered: { label: 'Delivered', tone: 'ok' },
  on_hold: { label: 'On hold', tone: 'warn' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
};

export function orderStatusLabel(status: string): string {
  return ORDER_STATUS[status]?.label ?? status;
}

export function orderStatusTone(status: string): Tone {
  return ORDER_STATUS[status]?.tone ?? 'neutral';
}

const ROLE_LABELS: Record<string, string> = {
  l1_admin: 'Administrator',
  l1_underwriter: 'Underwriter',
  l2_office: 'Office Principal',
  l3_manager: 'Agent Manager',
  l4_agent: 'Agent',
  merchant_support: 'Merchant Support',
};

/** What a node *is*, as opposed to what its level number says. */
const KIND_LABELS: Record<string, string> = {
  group: 'Group',
  processor: 'Processor',
  office: 'Office',
  agency: 'Agency',
  agent: 'Agent',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind.replace(/_/g, ' ');
}

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role.replace(/_/g, ' ');
}

const SCOPE_LABELS: Record<string, { short: string; title: string }> = {
  all: { short: 'All', title: 'Everything' },
  down: { short: 'Down', title: 'Self and children' },
  own: { short: 'Own', title: 'Own records only' },
  ask: { short: 'Ask', title: 'Needs the parent office' },
  none: { short: '—', title: 'Not held' },
};

export function scopeLabel(scope: string | undefined): { short: string; title: string } {
  return SCOPE_LABELS[scope ?? 'none'] ?? SCOPE_LABELS.none;
}
